import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import { IItinerary } from 'common/types';
import {
  FilterKeys,
  InternationalFilterContext,
} from 'contexts/internationalFilter';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { SearchType, TripType } from 'contexts/searchParams';
import useFlightsCards from 'hooks/useFlightsCards';
import useInternationalFilter from 'hooks/useInternationalFilter';
import {
  Checkbox,
  CheckboxGroup,
  DepartureTimeFilter,
  Footer,
  Header,
  Section,
  Tab,
  TabContent,
  TabPane,
} from 'list/common/filter';
import { Shape } from 'list/common/filter/Checkbox';
import { DepartureTime } from 'list/common/filter/DepartureTimeFilter';
import PriceRangeFilter from 'list/common/filter/PriceRange';
import { isSameAirportOnlyFilterAvailable } from 'list/international/filter/filterStore';
import sum from 'lodash/sum';
import uniq from 'lodash/uniq';
import { useContext } from 'react';
import AirportFilter from './airportFilter';
import AllianceFilter from './allianceFilter';
import DurationFilter from './durationFilter';
import FilterSet from './filterSet';
import styles from './index.module.scss';
import RangeFilter from './rangeFilter';
import { InternationalFilterTab } from './types';

const cx = classNames.bind(styles);

const tabNames = {
  [InternationalFilterTab.AIRPORT]: '공항',
  [InternationalFilterTab.VIA_COUNT]: '직항/경유',
  [InternationalFilterTab.FARE_TYPE]: '요금조건',
  [InternationalFilterTab.AIRLINE]: '항공사',
  [InternationalFilterTab.DURATION]: '시각/가격',
};

const tabClickCodes = {
  [InternationalFilterTab.AIRPORT]: 'ift.airport',
  [InternationalFilterTab.VIA_COUNT]: 'ift.stops',
  [InternationalFilterTab.FARE_TYPE]: 'ift.condition',
  [InternationalFilterTab.AIRLINE]: 'ift.airline',
  [InternationalFilterTab.DURATION]: 'ift.time',
};

const tabs: InternationalFilterTab[] = [
  InternationalFilterTab.AIRPORT,
  InternationalFilterTab.VIA_COUNT,
  InternationalFilterTab.FARE_TYPE,
  InternationalFilterTab.AIRLINE,
  InternationalFilterTab.DURATION,
];

const viaCountClickCodes = ['ift.stdirect', 'ift.st1', 'ift.st2'];

const getFareTypeGroups = (
  fareTypes: FilterSet<string>,
  flightsCardsMap: Map<string, [string, number]>,
) => {
  const fareTypeDetails = fareTypes.items.map(
    ({ label, value, enable, selected }) => {
      const [codeA, codeB] = value.split('/');
      const [nameA, ...nameB] = label.split('/');
      return {
        category: flightsCardsMap.get(codeB)?.[0] || '',
        codeA,
        codeB,
        nameA,
        nameB: nameB.join('/'),
        naverPaySaving: flightsCardsMap.get(codeB)?.[1] || 0,
        enable,
        selected,
      };
    },
  );

  const uniqueCodeAs = uniq(fareTypeDetails.map(({ codeA }) => codeA)).sort();

  return uniqueCodeAs.map(groupCode => {
    const codeAGroups = fareTypeDetails.filter(
      ({ codeA }) => groupCode === codeA,
    );

    // https://oss.navercorp.com/techminds/bts/issues/494#issuecomment-13086591
    // 카테고리 순서: "모든 결제수단" -> 신용카드(즉시할인) -> 간편결제 -> 신용카드(실적충족/청구할인)
    const categoryOrder = [
      '',
      '신용카드(즉시할인)',
      '간편결제',
      '신용카드(실적충족/청구할인)',
    ];

    const uniqueCategories = uniq(
      codeAGroups.map(({ category }) => category),
    ).sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      return indexA - indexB;
    });

    return uniqueCategories.map(groupCategory =>
      codeAGroups.filter(({ category }) => category === groupCategory),
    );
  });
};

function toggleState<T>(value: T, targetState: Set<T>) {
  if (targetState.has(value)) {
    targetState.delete(value);
  } else {
    targetState.add(value);
  }
}

const getFareCode = (codeA: string, codeB?: string) =>
  `${codeA}${codeB ? `/${codeB}` : ''}`;

const InternationalFilter = ({
  itinerary,
  tripType,
}: {
  itinerary: IItinerary[];
  tripType: TripType;
}) => {
  const {
    activeTab,
    filterState,
    filterStore,
    currentItinSeq,
    setActiveTab,
    setFilterModal,
    updateSelected,
    setFilterState,
    setFilterStates,
  } = useContext(InternationalFilterContext);

  const ref = useOffClick(() => {
    updateSelected();
    setFilterModal(false);
  });

  const { isKeeping, applyFilter, resetFilter, toggleKeeping } =
    useInternationalFilter(itinerary);

  const { withReloadTimer } = useReloadTimerContext();

  const flightsCards = useFlightsCards();

  const flightsCardsMap = new Map<string, [string, number]>(
    flightsCards.map(card => [card.code, [card.category, card.naverPaySaving]]),
  );

  const {
    airlineFilter,
    airportFilter,
    departureTimeFilters,
    durationFilters,
    fareTypeFilter,
    priceFilter,
    viaCountFilter,
  } = filterStore;

  const fareTypeGroups = getFareTypeGroups(fareTypeFilter, flightsCardsMap);

  const [
    airlineSelected,
    airportSelected,
    allianceSelected,
    departureTimeSelected,
    durationSelected,
    fareTypeSelected,
    priceSelected,
    sameAirlineOnlySelected,
    sameAirportOnlySelected,
    viaCountSelected,
  ] = [
    filterState[FilterKeys.airline],
    filterState[FilterKeys.airport],
    filterState[FilterKeys.alliance],
    filterState[FilterKeys.departureTime],
    filterState[FilterKeys.duration],
    filterState[FilterKeys.fareType],
    filterState[FilterKeys.price],
    filterState[FilterKeys.sameAirlineOnly],
    filterState[FilterKeys.sameAirportOnly],
    filterState[FilterKeys.viaCount],
  ];

  function confirm() {
    viaCountFilter.reset();
    fareTypeFilter.reset();
    airlineFilter.reset();
    airportFilter.forEach(filters => {
      filters.forEach(filter => {
        filter.reset();
      });
    });

    departureTimeFilters.forEach(el => el.reset());
    durationFilters.forEach(el => el.setFromTo([0, 0]));
    viaCountSelected.forEach(value => viaCountFilter.select(value));
    fareTypeSelected.forEach(value => fareTypeFilter.select(value));
    airlineSelected.forEach(value => airlineFilter.select(value));
    airportSelected.forEach((sets, index) => {
      sets.forEach((set, key) => {
        set.forEach(value => airportFilter[index][key].select(value));
      });
    });
    priceFilter.setFromTo(priceSelected.getFromTo());
    departureTimeSelected.map((selected, seq) =>
      selected.forEach(time => departureTimeFilters[seq].select(time)),
    );
    durationSelected.map((filter, seq) =>
      durationFilters[seq].setFromTo(filter.getFromTo()),
    );
    filterStore.sameAirlineOnlyFilter = sameAirlineOnlySelected;
    filterStore.sameAirportOnlyFilter = sameAirportOnlySelected;

    applyFilter(filterStore);
    setFilterModal(false);
  }

  function cancel() {
    updateSelected();
    setFilterModal(false);
  }

  function reset() {
    resetFilter();
    updateSelected();
  }

  function close() {
    updateSelected();
    setFilterModal(false);
  }

  function onViaCountChanged(value: number) {
    if (value === -1) {
      setFilterState(FilterKeys.viaCount, new Set<number>());
    } else {
      const targetState = new Set<number>(viaCountSelected);
      toggleState(value, targetState);
      setFilterState(FilterKeys.viaCount, targetState);
    }
  }

  function getTargetFareTypes(targetCodeA: string) {
    return fareTypeGroups
      .filter(group => group[0][0].codeA === targetCodeA)
      .flat()
      .flat()
      .map(({ codeA, codeB }) => getFareCode(codeA, codeB));
  }

  function onFareTypeGroupChanged(targetCodeA: string, isAdd: boolean) {
    const targetState = new Set<string>(fareTypeSelected);
    const targetFareTypes = getTargetFareTypes(targetCodeA);
    targetFareTypes.forEach(value => {
      if (isAdd) {
        if (!targetState.has(value)) {
          targetState.add(value);
        }
      } else {
        if (targetState.has(value)) {
          targetState.delete(value);
        }
      }
    });
    setFilterState(FilterKeys.fareType, targetState);
  }

  function onFareTypeChanged(value?: string) {
    if (!value) {
      setFilterState(
        FilterKeys.fareType,
        new Set<string>(fareTypeFilter.getValueSet()),
      );
    } else {
      const targetState = new Set<string>(fareTypeSelected);
      if (targetState.has(value)) {
        targetState.delete(value);
      } else {
        targetState.add(value);
      }
      setFilterState(FilterKeys.fareType, targetState);
    }
  }

  function onFareTypeReset() {
    setFilterState(FilterKeys.fareType, new Set<string>());
  }

  function onAirlineChanged(value?: string) {
    const newState: { [key: string]: any } = {
      [FilterKeys.alliance]: allianceSelected.map(() => false),
    };
    if (!value) {
      newState[FilterKeys.airline] = new Set<string>();
    } else {
      const targetState = new Set<string>(airlineSelected);
      toggleState(value, targetState);
      newState[FilterKeys.airline] = targetState;
    }
    newState[FilterKeys.sameAirlineOnly] = !!newState[FilterKeys.airline].size;
    setFilterStates(newState);
  }

  function onAirlineAllSelect() {
    setFilterState(
      FilterKeys.airline,
      new Set(airlineFilter.items.map(({ value }) => value)),
    );
  }

  function onDepartureTimeChanged(itinSeq: number, value?: DepartureTime) {
    const curSelected: Set<DepartureTime> =
      departureTimeSelected[itinSeq] || new Set();
    if (!value) {
      setFilterState(FilterKeys.departureTime, [
        ...departureTimeSelected.slice(0, itinSeq),
        new Set<DepartureTime>(),
        ...departureTimeSelected.slice(itinSeq + 1),
      ]);
    } else {
      if (curSelected.has(value)) {
        curSelected.delete(value);
      } else {
        curSelected.add(value);
      }
      setFilterState(FilterKeys.departureTime, [
        ...departureTimeSelected.slice(0, itinSeq),
        new Set<DepartureTime>(curSelected),
        ...departureTimeSelected.slice(itinSeq + 1),
      ]);
    }
  }

  function onDurationChanged(itinSeq: number, value: number) {
    const target = durationSelected[itinSeq];
    setFilterState(FilterKeys.duration, [
      ...durationSelected.slice(0, itinSeq),
      new RangeFilter(
        target.getFrom(),
        value,
        target.getMin(),
        target.getMax(),
      ),
      ...durationSelected.slice(itinSeq + 1),
    ]);
  }

  function onPriceChanged({
    min,
    max,
  }: {
    min: number | undefined;
    max: number | undefined;
  }) {
    setFilterState(
      FilterKeys.price,
      new RangeFilter(
        min || priceSelected.getMin(),
        max || priceSelected.getMax(),
        priceSelected.getMin(),
        priceSelected.getMax(),
      ),
    );
  }

  const airportFilterCount = airportFilter.flatMap(_filter =>
    _filter.flatMap(({ items }) => items.flat()),
  ).length;
  const selectedFilterCount = sum(
    airportSelected.flatMap(_filter => _filter.map(filter => filter.size)),
  );

  function selectAllAirports() {
    const allSelected = airportFilter.map(_filter =>
      _filter.map(({ items }) => new Set(items.map(({ value }) => value))),
    );

    setFilterState(FilterKeys.airport, allSelected);
  }

  function deselectAllAirports() {
    setFilterState(
      FilterKeys.airport,
      airportFilter.map(() => [new Set(), new Set()]),
    );
  }

  function toggleSameAirlineOnlyFilter() {
    setFilterState(FilterKeys.sameAirlineOnly, !sameAirlineOnlySelected);
  }

  function toggleSameAirportOnlyFilter() {
    setFilterState(FilterKeys.sameAirportOnly, !sameAirportOnlySelected);
  }

  return (
    <div
      className={cx('InternationalFilter')}
      role="dialog"
      aria-modal="true"
      data-modalOnlyMobile="true"
      ref={ref}
    >
      <div className={cx('content')}>
        <Header
          searchType={SearchType.INTERNATIONAL}
          onClose={close}
          onReset={reset}
        />
        <div className={cx('tabs')}>
          {tabs.map(id => (
            <Tab
              key={id}
              activeTab={activeTab}
              id={id}
              clickCode={tabClickCodes[id]}
              onClick={() => setActiveTab(id)}
            >
              {tabNames[id]}
            </Tab>
          ))}
        </div>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={InternationalFilterTab.AIRPORT}>
            <div className={cx('airport_section')}>
              <h5 className={cx('section_title')}>
                <span className={cx('text')}>공항</span>
                {isSameAirportOnlyFilterAvailable(itinerary) && (
                  <button
                    type="button"
                    role="checkbox"
                    className={cx('toggle_btn')}
                    aria-checked={sameAirportOnlySelected}
                    data-event-nlog="click"
                    data-event-area-code="ift.apinout"
                    onClick={toggleSameAirportOnlyFilter}
                  >
                    <span className={cx('text')}>
                      출도착 동일 공항 요금만 보기
                    </span>
                    <span className={cx('toggle')} />
                  </button>
                )}
              </h5>
              <div className={cx('subsection')}>
                <div className={cx('top_filter')}>
                  <span className={cx('title')}>전체</span>
                  <div className={cx('sub')}>
                    <button
                      type="button"
                      role="checkbox"
                      className={cx('toggle_all')}
                      data-event-nlog="click"
                      data-event-area-code="ift.apall"
                      disabled={airportFilterCount === selectedFilterCount}
                      onClick={selectAllAirports}
                    >
                      전체 선택
                    </button>
                    <button
                      type="button"
                      role="checkbox"
                      className={cx('toggle_all')}
                      data-event-nlog="click"
                      data-event-area-code="ift.apallcancel"
                      disabled={selectedFilterCount === 0}
                      onClick={deselectAllAirports}
                    >
                      전체 지우기
                    </button>
                  </div>
                </div>
              </div>
              <AirportFilter tripType={tripType} />
            </div>
          </TabPane>
          <TabPane tabId={InternationalFilterTab.VIA_COUNT}>
            <Section title={tabNames[InternationalFilterTab.VIA_COUNT]}>
              <div className={cx('radiogroup')} role="radiogroup">
                <button
                  type="button"
                  className={cx('checkbox')}
                  role="checkbox"
                  data-block="true"
                  data-shape="round"
                  aria-checked={viaCountSelected.size === 0}
                  data-event-nlog="click"
                  data-event-area-code="ift.stall"
                  onClick={() => onViaCountChanged(-1)}
                >
                  전체
                </button>
                {viaCountFilter.items.map((el, index) => (
                  <button
                    key={`viaCount${el.value}`}
                    type="button"
                    className={cx('checkbox')}
                    role="checkbox"
                    data-block="false"
                    data-shape="round"
                    aria-checked={viaCountSelected.has(el.value)}
                    data-event-nlog="click"
                    data-event-area-code={viaCountClickCodes[index]}
                    onClick={() => onViaCountChanged(el.value)}
                  >
                    <i className={cx('name')}>{el.label}</i>
                    {el.subLabel ? (
                      <b className={cx('price')}>
                        <i className={cx('num')}>
                          {withPriceSeperator(Number(el.subLabel))}
                        </i>
                        <span className={cx('unit')}>원~</span>
                      </b>
                    ) : (
                      <b className={cx('empty')}>조회 결과 없음</b>
                    )}
                  </button>
                ))}
              </div>
            </Section>
          </TabPane>
          <TabPane tabId={InternationalFilterTab.FARE_TYPE}>
            <div className={cx('fare_section')}>
              <h5 className={cx('section_title')}>
                <span className={cx('text')}>요금조건</span>
                <p className={cx('notice')}>
                  선택한 요금 조건은 지정된 결제수단만 결제 가능합니다
                </p>
              </h5>
              <div className={cx('subsection')}>
                <div className={cx('top_filter')}>
                  <span className={cx('title')}>전체</span>
                  <div className={cx('sub')}>
                    <button
                      type="button"
                      role="checkbox"
                      className={cx('toggle_all')}
                      data-event-nlog="click"
                      data-event-area-code="ift.cdall"
                      disabled={
                        fareTypeSelected.size ===
                        fareTypeFilter.getValueSet().size
                      }
                      onClick={() => onFareTypeChanged()}
                    >
                      전체 선택
                    </button>
                    <button
                      type="button"
                      role="checkbox"
                      className={cx('toggle_all')}
                      data-event-nlog="click"
                      data-event-area-code="ift.cdallcancel"
                      disabled={fareTypeSelected.size === 0}
                      onClick={() => onFareTypeReset()}
                    >
                      전체 지우기
                    </button>
                  </div>
                </div>
              </div>
              {fareTypeGroups.map(codeAGroup => {
                const { nameA, codeA } = codeAGroup[0][0];
                const allFareTypeSelected =
                  Array.from(fareTypeSelected).filter(fareType =>
                    fareType.includes(codeA),
                  ).length === getTargetFareTypes(codeA).length;
                return (
                  <div className={cx('subsection')} key={nameA}>
                    <div className={cx('top_filter')}>
                      <span className={cx('title')}>{nameA}</span>
                      <button
                        type="button"
                        role="checkbox"
                        className={cx('toggle_btn')}
                        aria-checked={allFareTypeSelected}
                        data-event-nlog="click"
                        data-event-area-code="ift.cdfareall"
                        onClick={() =>
                          onFareTypeGroupChanged(codeA, !allFareTypeSelected)
                        }
                      >
                        <span className={cx('text')}>{nameA} 전체</span>
                        <span className={cx('toggle')} />
                      </button>
                    </div>
                    <div className={cx('checkboxes')}>
                      {codeAGroup.map(codeBGroup => {
                        const { codeB, category } = codeBGroup[0];
                        if (!codeB)
                          return (
                            <div className={cx('subgrid')} key="general">
                              <Checkbox
                                block
                                isChecked={fareTypeSelected.has(
                                  getFareCode(codeA, codeB),
                                )}
                                shape={Shape.SQUARE}
                                clickCode="ift.cdsel"
                                onChange={() =>
                                  onFareTypeChanged(getFareCode(codeA, codeB))
                                }
                              >
                                모든 결제수단
                              </Checkbox>
                            </div>
                          );
                        return (
                          <div className={cx('subgrid')} key={codeB}>
                            <h6 className={cx('subtitle')} role="row">
                              {nameA}/{category}
                            </h6>
                            <CheckboxGroup
                              checked={fareTypeSelected}
                              commonClickCode="ift.cdsel"
                              options={codeBGroup.map(fareType => ({
                                // https://oss.navercorp.com/techminds/bts/issues/494#issuecomment-13086591
                                label: fareType.nameB.replace(
                                  '(이용실적 충족시)',
                                  '',
                                ),
                                value: getFareCode(
                                  fareType.codeA,
                                  fareType.codeB,
                                ),
                                naverPaySaving: fareType.naverPaySaving,
                              }))}
                              shape={Shape.SQUARE}
                              onChange={value => onFareTypeChanged(value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabPane>
          <TabPane tabId={InternationalFilterTab.AIRLINE}>
            <div className={cx('airline_section')}>
              <div className={cx('section_title_area')}>
                <h5 className={cx('section_title')}>항공사</h5>
                <div className={cx('sub', 'sub_airline')}>
                  <button
                    type="button"
                    role="checkbox"
                    className={cx('toggle_btn')}
                    aria-checked={sameAirlineOnlySelected}
                    data-event-nlog="click"
                    data-event-area-code="ift.alonlyairline"
                    onClick={toggleSameAirlineOnlyFilter}
                  >
                    <span className={cx('text')}>
                      동일한 항공사 조합만 보기
                    </span>
                    <span className={cx('toggle')} />
                  </button>
                </div>
                <div className={cx('radiogroup')} role="radiogroup">
                  <AllianceFilter airlineFilter={airlineFilter} />
                </div>
              </div>
              <div className={cx('checkboxes')}>
                <div className={cx('sub')} role="row">
                  <button
                    type="button"
                    role="checkbox"
                    className={cx('toggle_all')}
                    data-event-nlog="click"
                    data-event-area-code="ift.alall"
                    disabled={
                      airlineSelected.size === airlineFilter.items.length
                    }
                    onClick={() => onAirlineAllSelect()}
                  >
                    전체 선택
                  </button>
                  <button
                    type="button"
                    role="checkbox"
                    className={cx('toggle_all')}
                    data-event-nlog="click"
                    data-event-area-code="ift.alallcancel"
                    disabled={airlineSelected.size === 0}
                    onClick={() => onAirlineChanged()}
                  >
                    전체 지우기
                  </button>
                </div>
                <CheckboxGroup
                  checked={airlineSelected}
                  commonClickCode="ift.alsel"
                  options={airlineFilter.items.sort((a, b) =>
                    a.label.localeCompare(b.label, 'ko-KR'),
                  )}
                  shape={Shape.SQUARE}
                  onChange={value => onAirlineChanged(value)}
                />
              </div>
            </div>
          </TabPane>
          <TabPane tabId={InternationalFilterTab.DURATION}>
            <Section title="출발시각">
              <DepartureTimeFilter
                key="durationTime_0"
                title={
                  tripType === TripType.RT
                    ? '가는 날'
                    : `${currentItinSeq + 1}여정`
                }
                areaCode="ift"
                seq={(currentItinSeq + 1).toString()}
                selected={departureTimeSelected[currentItinSeq]}
                onChange={value =>
                  onDepartureTimeChanged(currentItinSeq, value)
                }
                onSelectAll={() => onDepartureTimeChanged(currentItinSeq)}
              />
              {tripType === TripType.RT && (
                <DepartureTimeFilter
                  key="durationTime_1"
                  title="오는 날"
                  areaCode="ift"
                  seq="2"
                  selected={departureTimeSelected[1]}
                  onChange={value => onDepartureTimeChanged(1, value)}
                  onSelectAll={() => onDepartureTimeChanged(1)}
                />
              )}
            </Section>
            <Section title="소요시간">
              {durationFilters[currentItinSeq] && (
                <DurationFilter
                  label={
                    <i className={cx('current', 'as_duration')}>
                      {tripType === TripType.RT
                        ? '가는날'
                        : `${currentItinSeq + 1}여정`}
                    </i>
                  }
                  clickCode={`ift.tm${currentItinSeq + 1}dur`}
                  min={durationFilters[currentItinSeq].getMin()}
                  max={durationFilters[currentItinSeq].getMax()}
                  current={durationSelected[currentItinSeq]?.getTo()}
                  setValue={(value: number) =>
                    onDurationChanged(currentItinSeq, value)
                  }
                />
              )}
              {tripType === TripType.RT && durationFilters[1] && (
                <DurationFilter
                  label={<i className={cx('current', 'as_duration')}>오는날</i>}
                  clickCode="ift.tm2dur"
                  min={durationFilters[1].getMin()}
                  max={durationFilters[1].getMax()}
                  current={durationSelected[1]?.getTo()}
                  setValue={(value: number) => onDurationChanged(1, value)}
                />
              )}
            </Section>
            <Section title="가격대">
              <PriceRangeFilter
                areaCode="ift"
                min={priceFilter.getMin()}
                max={priceFilter.getMax()}
                selectedRange={{
                  min: priceSelected.getFrom(),
                  max: priceSelected.getTo(),
                }}
                onAfterChange={([changedMin, changedMax]: number[]) =>
                  onPriceChanged({
                    min:
                      changedMin > priceFilter.getMin()
                        ? changedMin
                        : undefined,
                    max:
                      changedMax < priceFilter.getMax()
                        ? changedMax
                        : undefined,
                  })
                }
              />
            </Section>
          </TabPane>
        </TabContent>
        <Footer
          isChecked={isKeeping}
          searchType={SearchType.INTERNATIONAL}
          tooltip="선택하신 국제선 검색 조건을 저장합니다.\n공항 필터는 제외됩니다."
          onCancel={cancel}
          onConfirm={withReloadTimer(confirm)}
          onToggle={toggleKeeping}
          disabledSubmit={
            selectedFilterCount === 0 ||
            fareTypeSelected.size === 0 ||
            airlineSelected.size === 0
          }
        />
      </div>
    </div>
  );
};

export default InternationalFilter;
